import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import useUserProfile from '../../../../../customHooks/useUserProfile';
import IconButton from '../../../../../muiComponents/IconButton';
import Title from '../../../../components/Title';
import {
    MemberState,
    ZinantisMemberDescriptionState,
    ZinantisMemberHotelsState,
} from '../../../../../types/zinantis/members/membersType';
import DotIcon from '../../../../components/DotIcon';
import Image from '../../../../components/Image';
import Grid from '../../../../../muiComponents/Grid';
import GridChild from '../../../../../muiComponents/GridChild';
import Menu from '../../../components/Menu';
import encodeBase64 from '../../../../../utils/encodeBase64';
import Flag from '../../../components/Flag';
import api from '../../../../../services/api';
import { viewModelEntities } from '../../../../../utils/commonVariables/entityKeys';
import endpoints from '../../../../../utils/commonVariables/endpoints';
import { viewModelZinantisMemberDetail } from '../../../../../utils/viewModelStructures/zinantisMemberDetail';
import { IAgencyDescription } from '../../../../../types/entities/IAgencyDescription';
import { adaptMemberDescriptionToState } from '../../../../../services/zinantis/members/zinantisMemberListAdapters';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import {
    formatDate,
    getPastDate,
    isMinValueDate,
} from '../../../../../utils/formatDates';
import variables from '../../../../../stylesheets/variables.module.scss';
import Tag from '../../../../../muiComponents/Tag';
import LoadingPlaceholder from '../../../../../muiComponents/LoadingPlaceholder';
import Card from '../../../../../muiComponents/Card';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';

interface MemberPanelProps {
    open: boolean;
    onClose: () => void;
    memberId: string;
    isConnected: boolean;
    memberHotels: ZinantisMemberHotelsState[];
}

export const gridColumnsSpacing = {
    xs: '30px',
    sm: '30px',
    lg: '30px',
    xl: '30px',
};

export const gridRowsSpacing = {
    xs: '20px',
    sm: '20px',
    lg: '20px',
    xl: '20px',
};

export const doubleColumnGrid = { xs: 12, sm: 12, md: 6, lg: 6, xl: 6 };
export const fullColumnGrid = { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 };

const GENERAL_ERROR_FETCHING =
    'an_error_occurred_while_processing_your_request';

const MemberPanel = (props: MemberPanelProps) => {
    const {
        open,
        onClose,
        memberId,
        isConnected = false,
        memberHotels = [],
    } = props;
    const { translate, language } = useUserProfile();
    const { setErrorMessage } = useNotification();

    const [member, setMember] = useState<Partial<MemberState> | null>(null);
    const [loading, setLoading] = useState(false);
    const [visibleDescription, setVisibleDescription] =
        useState<ZinantisMemberDescriptionState | null>(null);

    useEffect(() => {
        if (!open) return;
        getMember();
    }, []);

    const getMember = useCallback(async () => {
        try {
            setLoading(true);
            const endpoint = endpoints.viewModel(
                viewModelEntities.Agency,
                memberId
            );
            const {
                data: { Result: memberData },
            } = await api.post(endpoint, viewModelZinantisMemberDetail);

            const twoMonthsAgo = getPastDate(2, 'month');

            const isNew = memberData.ZinantisMemberDate
                ? new Date(memberData.ZinantisMemberDate) >=
                  new Date(twoMonthsAgo)
                : false;

            setMember({
                id: memberData.Id,
                name: memberData.Name || '',
                address: memberData.Address || '',
                country:
                    memberData.Country?.LocalizedCaption ||
                    memberData.Country?.Caption ||
                    memberData.Country?.Name ||
                    '',
                countryCode: memberData.Country?.ISO2 || '',
                idCountry: memberData.IdCountry || 0,
                logo: memberData.LogoFile?.VirtualPath || '',
                idAccountManagmentSegment:
                    memberData.IdAccountManagementSegment || 0,
                accountManagmentSegment:
                    memberData.AccountManagementSegment?.LocalizedCaption ||
                    memberData.AccountManagementSegment?.Caption ||
                    memberData.AccountManagementSegment?.Name ||
                    '',
                descriptions: memberData.Descriptions.map(
                    (descr: IAgencyDescription) =>
                        adaptMemberDescriptionToState(descr)
                ),
                website: memberData.WebSite || '',
                isNew,
                bookingCodePrefix: memberData.BookingCodePrefix || '',
                signedDate: memberData.ZinantisMemberDate || null,
            } as MemberState);
        } catch (error) {
            setErrorMessage(translate(GENERAL_ERROR_FETCHING));
        } finally {
            setLoading(false);
        }
    }, [memberId]);

    const getInitialDescription = () => {
        if (!member || !member?.descriptions?.length) return null;
        const descriptionInAppLanguage = member.descriptions.find(
            desc => desc.code === language
        );
        const descriptionInDefaultLanguage = member.descriptions.find(
            desc => desc.code === 'en'
        );
        return (
            descriptionInAppLanguage ||
            descriptionInDefaultLanguage ||
            member.descriptions[0]
        );
    };

    useEffect(() => {
        setVisibleDescription(getInitialDescription());
    }, [member]);

    const descriptions = useMemo(() => {
        return member?.descriptions
            ? member.descriptions.map(desc => ({
                  Id: desc.id,
                  label: desc.language,
              }))
            : [];
    }, [member]);

    const formatExternalUrl = (url: string) => {
        if (!url) return '#';
        if (!/^https?:\/\//i.test(url)) {
            return `https://${url}`;
        }
        return url;
    };

    return (
        <LateralPanel open={open} onClose={onClose}>
            <div className='flex flex-end '>
                <IconButton iconName='Close' onClick={onClose} />
            </div>
            {loading ? (
                <div className='flex flex-column gap-20 padding20 width100perc'>
                    <LoadingPlaceholder />
                    <LoadingPlaceholder />
                    <LoadingPlaceholder />
                    <LoadingPlaceholder />
                    <LoadingPlaceholder />
                </div>
            ) : (
                <section className='flex-column gap-40'>
                    <div className='flex-column gap-20'>
                        <Grid
                            columnSpacing={gridColumnsSpacing}
                            rowSpacing={gridRowsSpacing}
                            style={{
                                borderBottom: '1px solid var(--border)',
                            }}
                        >
                            <GridChild gridProps={doubleColumnGrid}>
                                <div
                                    className='flex flex-column  flex-end'
                                    style={{ height: '100%' }}
                                >
                                    <div className='flex gap-20'>
                                        <h1 className='lateral-panel__title '>
                                            {member?.name}
                                        </h1>
                                        {member?.isNew && (
                                            <Tag
                                                text='new'
                                                color={
                                                    variables.accentColorPink400
                                                }
                                            />
                                        )}
                                    </div>
                                    <div className='flex flex-align-center gap-5'>
                                        <DotIcon
                                            truthy={isConnected}
                                            falsy={!isConnected}
                                        />
                                        <p style={{ fontSize: '14px' }}>
                                            {translate(
                                                isConnected
                                                    ? 'connected'
                                                    : 'disconnected'
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </GridChild>
                            {member?.logo && (
                                <GridChild gridProps={doubleColumnGrid}>
                                    <div
                                        className='flex flex-center flex-align-center padding10'
                                        style={{
                                            maxHeight: '180px',
                                            maxWidth: '100%',
                                            height: '180px',

                                            backgroundColor:
                                                'var(--always-white)',
                                        }}
                                    >
                                        <Image
                                            src={member.logo}
                                            alt={member.name}
                                            style={{
                                                maxWidth: '100%',
                                                maxHeight: '100%',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </div>
                                </GridChild>
                            )}
                        </Grid>
                        <Grid
                            columnSpacing={gridColumnsSpacing}
                            rowSpacing={gridRowsSpacing}
                            style={{ width: '100%' }}
                        >
                            {visibleDescription ? (
                                <GridChild gridProps={fullColumnGrid}>
                                    <div className='flex-column gap-5'>
                                        <div className='flex flex-align-end flex-between'>
                                            <Title>
                                                {translate('description2')}
                                            </Title>
                                            <Select
                                                size='small'
                                                value={visibleDescription.id}
                                                onChange={(
                                                    e: SelectChangeEvent
                                                ) => {
                                                    const idValue =
                                                        e.target.value;
                                                    const newDescription =
                                                        member?.descriptions
                                                            ? member?.descriptions.find(
                                                                  desc =>
                                                                      desc.id ===
                                                                      idValue?.toString()
                                                              ) || null
                                                            : null;
                                                    setVisibleDescription(
                                                        newDescription
                                                    );
                                                }}
                                            >
                                                {descriptions.map(desc => (
                                                    <MenuItem
                                                        key={desc.Id}
                                                        value={desc.Id}
                                                    >
                                                        {desc.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </div>
                                        <p>{visibleDescription.description}</p>
                                    </div>
                                </GridChild>
                            ) : (
                                ''
                            )}
                            {member?.address ? (
                                <GridChild gridProps={doubleColumnGrid}>
                                    <>
                                        <Title>{translate('address')}</Title>
                                        <p>{member?.address || ''}</p>
                                    </>
                                </GridChild>
                            ) : (
                                ''
                            )}

                            {member?.countryCode && member?.country && (
                                <GridChild gridProps={doubleColumnGrid}>
                                    <>
                                        <Title>{translate('country')}</Title>
                                        <div className='flex flex-align-center gap-5'>
                                            <Flag
                                                text={member.country}
                                                code={member.countryCode}
                                            />
                                            <p>{member.country}</p>
                                        </div>
                                    </>
                                </GridChild>
                            )}
                            {member?.idAccountManagmentSegment &&
                                member?.accountManagmentSegment && (
                                    <GridChild gridProps={doubleColumnGrid}>
                                        <>
                                            <Title>
                                                {translate('segment')}
                                            </Title>
                                            <p>
                                                {
                                                    member?.accountManagmentSegment
                                                }
                                            </p>
                                        </>
                                    </GridChild>
                                )}
                            {member?.website && (
                                <GridChild gridProps={doubleColumnGrid}>
                                    <>
                                        <Title>{translate('website')}</Title>
                                        <a
                                            className='member-panel__website'
                                            href={formatExternalUrl(
                                                member.website
                                            )}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            {member.website}
                                        </a>
                                    </>
                                </GridChild>
                            )}
                            {member?.bookingCodePrefix && (
                                <GridChild gridProps={doubleColumnGrid}>
                                    <>
                                        <Title>
                                            {translate('booking_code_prefix')}
                                        </Title>
                                        <p>{member?.bookingCodePrefix}</p>
                                    </>
                                </GridChild>
                            )}
                            {member?.signedDate &&
                                !isMinValueDate(member?.signedDate) && (
                                    <GridChild gridProps={doubleColumnGrid}>
                                        <>
                                            <Title>
                                                {translate('member_since')}
                                            </Title>
                                            <p>
                                                {formatDate(
                                                    member?.signedDate,
                                                    language
                                                )}
                                            </p>
                                        </>
                                    </GridChild>
                                )}
                        </Grid>
                    </div>
                    {memberHotels && memberHotels.length ? (
                        <div className='flex flex-column gap-20'>
                            <h2 className='lateral-panel__title '>
                                {translate('connected-hotels')}
                            </h2>
                            <ul className='flex flex-wrap gap-20 overflow-y-auto'>
                                {memberHotels.map(hotel => {
                                    return (
                                        <li key={hotel.id}>
                                            <Card
                                                style={{ cursor: 'auto' }}
                                                className='member-card__container padding10 gap-10'
                                            >
                                                <div
                                                    className='flex-grow'
                                                    style={{
                                                        borderRadius: '5px',
                                                        overflow: 'hidden',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Image
                                                        useLocalBaseUrl={false}
                                                        src={hotel.image || ''}
                                                        alt={member?.name}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                </div>
                                                <div className='flex flex-between flex-align-center gap-10 width100perc'>
                                                    <Title className='flex-grow '>
                                                        {hotel.hotelName}
                                                    </Title>
                                                    <Menu
                                                        menuItems={[
                                                            {
                                                                text: 'open-hotel-details',
                                                                icon: 'OpenInNew',
                                                                onClick: () => {
                                                                    const url = `/your-property/${encodeBase64(
                                                                        hotel.idSupplierHotel
                                                                    )}/`;
                                                                    window.open(
                                                                        url,
                                                                        url
                                                                    );
                                                                },
                                                            },
                                                        ]}
                                                    />
                                                </div>
                                            </Card>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ) : (
                        ''
                    )}
                </section>
            )}
        </LateralPanel>
    );
};

export default MemberPanel;
